// Data Service - Easy to switch between different data sources
class DataService {
    constructor() {
        // You can switch between 'json', 'sheets-csv', or 'sheets-api'
        this.dataSource = 'sheets-csv';
    }

    // Main method to fetch race data
    async fetchRaceData(raceName) {
        switch (this.dataSource) {
            case 'json':
                return this.fetchFromJSON(raceName);
            case 'sheets-csv':
                return this.fetchFromGoogleSheetsCSV(raceName);
            case 'sheets-api':
                return this.fetchFromGoogleSheetsAPI(raceName);
            default:
                throw new Error('Invalid data source');
        }
    }

    // Option 1: JSON Files (Easiest to start with)
    async fetchFromJSON(raceName) {
        try {
            const response = await fetch(`/data/${raceName}.json`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching JSON data:', error);
            return { records: [] };
        }
    }

    // Option 2: Google Sheets CSV (Recommended for easy management)
    async fetchFromGoogleSheetsCSV(raceName) {
        // Using Google Sheets Published Web URL to avoid CORS issues
        // To get this URL: File -> Share -> Publish to web -> Choose CSV format
        const sheetUrls = {
            // IMPORTANT: After publishing your sheet, replace these URLs with the published versions
            // To publish: File → Share → Publish to web → Choose CSV format

            // Your Sheet ID: 1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k
            // Published URLs (no CORS issues):
            '2025-aug-23': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=0',
            '2025-aug-30': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=2078349513',
            '2025-sep-6': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=1628668752',
            '2025-sep-13': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=825217863',
            '2025-sep-20': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=1620807181',
            '2025-sep-27': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=1586674775',
            '2025-oct-4': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=1429732476',
            '2025-nov-1': 'https://docs.google.com/spreadsheets/d/1WjUAr4pF1oRZaqulMkZzLMoVsO7KQmzA9KG6nfcp54k/export?format=csv&gid=855938366',
        };

        const url = sheetUrls[raceName];
        if (!url) {
            console.error(`No sheet URL configured for race: ${raceName}`);
            return { records: [] };
        }

        try {
            console.log('Fetching from URL:', url);

            // For published sheets, we can fetch directly without CORS issues
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'text/csv'
                }
            });

            if (!response.ok) {
                console.error('HTTP error:', response.status, response.statusText);

                // Try fallback to JSON
                console.log('Attempting fallback to JSON data source...');
                return this.fetchFromJSON(raceName);
            }

            const csvText = await response.text();
            console.log('CSV fetch successful, length:', csvText.length);

            // Parse CSV to our expected format
            const result = this.parseCSVToAirtableFormat(csvText);
            console.log('Parsed result:', result);
            return result;
        } catch (error) {
            console.error('Error fetching CSV data:', error);
            console.log('Attempting fallback to JSON data source...');

            // Fallback to JSON if CSV fails
            return this.fetchFromJSON(raceName);
        }
    }

    // Option 3: Google Sheets API (requires API key)
    async fetchFromGoogleSheetsAPI(raceName) {
        // You'll need to set up Google Sheets API and get an API key
        const API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY;
        const SHEET_ID = process.env.REACT_APP_GOOGLE_SHEET_ID;

        if (!API_KEY || !SHEET_ID) {
            console.error('Google Sheets API key or Sheet ID not configured');
            return { records: [] };
        }

        const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/${raceName}?key=${API_KEY}`;

        try {
            const response = await fetch(url);
            const data = await response.json();
            return this.parseGoogleSheetsToAirtableFormat(data);
        } catch (error) {
            console.error('Error fetching Google Sheets data:', error);
            return { records: [] };
        }
    }

    // Helper: Parse CSV to match Airtable format
    parseCSVToAirtableFormat(csvText) {
        console.log('Raw CSV data:', csvText.substring(0, 500));
        const lines = csvText.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
            console.log('Not enough lines in CSV:', lines.length);
            return { records: [] };
        }

        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        console.log('CSV Headers:', headers);
        const records = [];

        for (let i = 1; i < lines.length; i++) {
            const values = this.parseCSVLine(lines[i]);
            if (values.length === headers.length) {
                const fields = {};
                headers.forEach((header, index) => {
                    fields[header] = values[index];
                });

                // Handle Avatar field (expecting JSON string or URL)
                if (fields.Avatar) {
                    console.log('Processing Avatar field for', fields.Name, ':', fields.Avatar);
                    try {
                        fields.Avatar = JSON.parse(fields.Avatar);
                        console.log('Successfully parsed Avatar as JSON:', fields.Avatar);
                    } catch {
                        // Handle Airtable attachment format or simple URL
                        if (fields.Avatar.includes('airtable.com') || fields.Avatar.includes('airtableusercontent.com')) {
                            // Extract URL from Airtable attachment format like "filename.jpg (https://...)"
                            const urlMatch = fields.Avatar.match(/https:\/\/[^)\s]+/);
                            if (urlMatch) {
                                fields.Avatar = [{ url: urlMatch[0] }];
                                console.log('Extracted Airtable URL:', urlMatch[0]);
                            } else {
                                // If no URL found, set to null so component can handle gracefully
                                fields.Avatar = null;
                                console.log('No URL found in Airtable attachment:', fields.Avatar);
                            }
                        } else if (fields.Avatar.startsWith('http')) {
                            // If it's a direct URL, wrap it
                            fields.Avatar = [{ url: fields.Avatar }];
                            console.log('Using direct URL:', fields.Avatar);
                        } else {
                            // Invalid format, set to null
                            fields.Avatar = null;
                            console.log('Invalid Avatar format:', fields.Avatar);
                        }
                    }
                } else {
                    // Avatar field is empty or undefined
                    fields.Avatar = null;
                }

                records.push({ fields });
            }
        }

        return { records };
    }

    // Helper: Parse Google Sheets API response to Airtable format
    parseGoogleSheetsToAirtableFormat(data) {
        if (!data.values || data.values.length < 2) return { records: [] };

        const headers = data.values[0];
        const records = [];

        for (let i = 1; i < data.values.length; i++) {
            const row = data.values[i];
            const fields = {};

            headers.forEach((header, index) => {
                fields[header] = row[index] || '';
            });

            // Handle Avatar field
            if (fields.Avatar) {
                try {
                    fields.Avatar = JSON.parse(fields.Avatar);
                } catch {
                    fields.Avatar = [{ url: fields.Avatar }];
                }
            }

            records.push({ fields });
        }

        return { records };
    }

    // Helper: Parse CSV line handling quotes and commas
    parseCSVLine(line) {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        result.push(current.trim());
        return result.map(val => val.replace(/^"|"$/g, ''));
    }

    // Method to switch data sources
    setDataSource(source) {
        this.dataSource = source;
    }
}

export default new DataService();
